import moment, { Moment } from "moment";
import { HistoryItemKeyEnum, SearchEnums, TimeEnum } from "../../../lib/enums";
import { enums } from "../../../shim/jsbridge";
import { underscoreToTitleCase } from "../../../utils";
import { removeSurroundingQuotes } from "../../network/util";
import { Selections } from "../@types";
import {
  CLOUD_LIMIT_MOMENT,
  FIRST_COLON_ONLY_REGEX,
  FIRST_SEEN_STRING,
  ISO_FORMAT_WITHOUT_MILLIS,
  MATCH_FIRST_SEEN,
  MATCH_FIRST_SEEN_USER_FRIENDLY_DATE,
  MATCH_FIRST_SEEN_USER_FRIENDLY_SHORT
} from "../consts";
import { getNtiValue, isBasicSearch } from "../util";
import { UNIT_MAP } from "./consts";

const { analyzer: { TISCALE = 0 } = {} } = enums || {};

type KeyofSelections = keyof Selections;

const getEndOfTheDay = () =>
  moment().utc().endOf("day").format(ISO_FORMAT_WITHOUT_MILLIS);

const getCloudTimeZero = () =>
  CLOUD_LIMIT_MOMENT.format(ISO_FORMAT_WITHOUT_MILLIS);

const getFilterClass = (value: string) => {
  if (value) {
    return "applied-nonempty-selection";
  }
  return "";
};

const getActiveDropdownValue = (
  selections: Selections,
  keyword: string
): string => selections[keyword as KeyofSelections]?.label || "";

const getSearchQuery = (query: string, transformedQuery: string) => {
  const isBasic = isBasicSearch(query);
  if (!isBasic) return transformedQuery;

  const [keyword, value] = transformedQuery.split(FIRST_COLON_ONLY_REGEX);

  // if keyword is one of the basic search reroute keywords, return query without a keyword
  // this is needed for reroute
  if (["ipv4", "ipv6", "domain", "uri"].includes(keyword)) {
    return value;
  }
  return transformedQuery;
};

export const isSummaryAvailable = (
  analyzer: number,
  processingStatus: string,
  firstSeen: string | Date
) =>
  !(TISCALE === analyzer && processingStatus === "ERROR" && !firstSeen) &&
  processingStatus !== "PROCESSING";

export const getTimerangeFromEnum = (value: string) => {
  const TIMERANGE_MAP: Record<string, Moment> = {
    [TimeEnum.TODAY]: moment(),
    [TimeEnum.LAST_WEEK]: moment().subtract(1, "weeks"),
    [TimeEnum.LAST_MONTH]: moment().subtract(1, "months"),
    [TimeEnum.LAST_QUARTER]: moment().subtract(3, "months"),
    [TimeEnum.LAST_YEAR]: moment().subtract(1, "years")
  };

  const timerangeEndDay = getEndOfTheDay();
  const timerangeStartDay = TIMERANGE_MAP[value]
    .utc()
    .startOf("day")
    .format(ISO_FORMAT_WITHOUT_MILLIS);

  return `[${timerangeStartDay} TO ${timerangeEndDay}]`;
};

export const getSelectionPresentationKey = (key: string) =>
  HistoryItemKeyEnum[key as keyof typeof HistoryItemKeyEnum] ?? key;

export const getSelectionPresentationValue = (key: string, value: string) => {
  if (key === "sample_origin") {
    return value.split(",").map(underscoreToTitleCase).join(", ");
  }

  const searchEnum = SearchEnums[key as keyof typeof SearchEnums];

  return searchEnum
    ? searchEnum[value as keyof typeof searchEnum] || value
    : value;
};

/**
 * Check if the query is a simple query. A simple query is a
 * query that does not contain keywords "OR" or "NOT".
 * @param query - The query to check
 * @returns True if the query is simple, false otherwise
 */
export const queryIsSimple = (query: string) =>
  !query.includes("OR") && !query.includes("NOT");

export const hasOnlyOneInstanceOfFirstSeen = (query: string) =>
  query.split(FIRST_SEEN_STRING).length === 2;

export const canExtractFirstSeen = (query: string) =>
  queryIsSimple(query) && hasOnlyOneInstanceOfFirstSeen(query);

export const getIsFirstSeenSynced = (q: string, selections: Selections) =>
  canExtractFirstSeen(q ?? "") && selections?.time_type?.value === "firstseen";

const userFriendlyDateFormatToDate = (value?: string) => {
  if (!value) return "";
  const lastChar = value.at(-1);
  const timerangeEndDay = getEndOfTheDay();
  const cloudTimeZero = getCloudTimeZero();

  const date = value.slice(0, -1).toLowerCase().endsWith("Z")
    ? value.slice(0, -1)
    : `${value.slice(0, -1)}Z`;

  if (lastChar === "+") return `[${date} TO ${timerangeEndDay}]`;
  return `[${cloudTimeZero} TO ${date}]`;
};

const userFriendlyFormatToDate = (value?: string) => {
  if (!value) return "";
  const match = value.match(/(\d+)([hdwmy])([+-])/i);

  if (!match) return "";

  const amount = match.at(1);
  const unit = match.at(2) ?? "";

  const momentUnit = UNIT_MAP[unit];

  const endOfTheDay = getEndOfTheDay();
  const cloudTimeZero = getCloudTimeZero();

  const date = moment()
    .utc()
    .subtract(amount, momentUnit)
    .format(ISO_FORMAT_WITHOUT_MILLIS);

  if (match.at(-1) === "+") return `[${date} TO ${endOfTheDay}]`;
  return `[${cloudTimeZero} TO ${date}]`;
};

export const extractFirstSeen = (query: string) => {
  const firstSeen = query.match(MATCH_FIRST_SEEN);
  if (firstSeen) return firstSeen.at(2) ?? "";

  const firstSeenUserFriendlyDate = query.match(
    MATCH_FIRST_SEEN_USER_FRIENDLY_DATE
  );

  if (firstSeenUserFriendlyDate)
    return userFriendlyDateFormatToDate(firstSeenUserFriendlyDate.at(2)) ?? "";

  const firstSeenUserFriendly = query.match(
    MATCH_FIRST_SEEN_USER_FRIENDLY_SHORT
  );

  if (firstSeenUserFriendly)
    return userFriendlyFormatToDate(firstSeenUserFriendly.at(2));
  return "";
};

const replaceUserFriendlyShort = (query: string, value: string) =>
  query.replace(MATCH_FIRST_SEEN_USER_FRIENDLY_SHORT, `$1${value}`);

const replaceUserFriendlyDate = (query: string, value: string) =>
  query.replace(MATCH_FIRST_SEEN_USER_FRIENDLY_DATE, `$1${value}`);

const replaceDate = (query: string, value: string) =>
  query.replace(MATCH_FIRST_SEEN, `$1${value}`);

export const replaceFirstSeen = (
  query: string,
  replacementFirstSeen: string
) => {
  if (query.match(MATCH_FIRST_SEEN))
    return replaceDate(query, replacementFirstSeen);
  if (query.match(MATCH_FIRST_SEEN_USER_FRIENDLY_DATE))
    return replaceUserFriendlyDate(query, replacementFirstSeen);
  return replaceUserFriendlyShort(query, replacementFirstSeen);
};

const getNtiValueFromQuery = (query: string) => {
  const ntiValue = getNtiValue(query);
  if (!ntiValue) return "";
  const [, value] = ntiValue.split(FIRST_COLON_ONLY_REGEX);
  return removeSurroundingQuotes(value);
};

export const refangUrlInText = (text: string): string =>
  text
    .replace(/h[t|x][t|x]p(s)?\[?:\/\/\]?/gi, (_, s) =>
      s ? "https://" : "http://"
    )
    .replace(/\[\.\]/g, ".")
    .replace(/\[\/\]/g, "/");

export const isNumeric = (val: number | string) =>
  typeof val === "string" && val.trim() !== ""
    ? Number.isFinite(+val)
    : Number.isFinite(val);

export const removeIllegalCharacters = (value: string | number) => {
  if (isNumeric(value)) return value;
  if (!value || typeof value !== "string") return "";
  return value.replaceAll(/[\r\n]/g, "");
};

export const formatQueryValue = (value: string) =>
  isNumeric(value) ? value : `"${value}"`;

export const applyQueryTransformations = (
  value: string | number,
  transformations: ((val: string | number) => string | number)[]
) => transformations.reduce((acc, fn) => fn(acc), value);

export {
  getActiveDropdownValue,
  getFilterClass,
  getNtiValueFromQuery,
  getSearchQuery
};
